import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Check } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { TricolorStripe } from '../components/TricolorStripe';
import { productDetails, sizeCategories } from '../productData';

// Hard goods where uniform customization does not apply
const NON_CUSTOMIZABLE_SLUGS = ['acc-ball-pump', 'acc-whistle', 'acc-cones', 'acc-ball-bag', 'acc-bottle', 'acc-shin-guards', 'acc-knee-pads', 'coach-starter-kit'];
const NON_CUSTOMIZABLE_CATEGORIES = ['Balls'];

export default function ProductDetailPage() {
  const { slug } = useParams();
  const { lang, t } = useLanguage();
  const navigate = useNavigate();
  const goBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/catalogue');
    }
  };
  const product = productDetails[slug];
  const [activeColor, setActiveColor] = useState(0);
  const [activeSize, setActiveSize] = useState(null);
  const [activeExtra, setActiveExtra] = useState(null);
  const [activeConfigIdx, setActiveConfigIdx] = useState(0);
  const [bundleQty, setBundleQty] = useState({ cones: 1, bibs: 12, balls: 5 });

  if (!product) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex flex-col items-center justify-center px-6 pt-32" data-testid="pdp-not-found">
        <p className="font-bebas text-3xl text-white tracking-wide">{t.pdp.notFound}</p>
        <Link
          to="/catalogue"
          className="mt-6 inline-flex items-center gap-2 bg-[#CE2B37] text-white font-bebas text-sm tracking-wider px-6 py-3 hover:bg-[#a82230] transition-colors"
          data-testid="pdp-back-to-catalogue"
        >
          <ArrowLeft size={16} /> {t.pdp.back}
        </Link>
      </div>
    );
  }

  const loc = product[lang];
  const canCustomize = !NON_CUSTOMIZABLE_SLUGS.includes(slug) && !NON_CUSTOMIZABLE_CATEGORIES.includes(product.category);
  const color = product.colors[activeColor];
  const configs = product.configurations || [];
  const activeConfigKey = configs[activeConfigIdx]?.key;
  const colorImage = color.images && activeConfigKey ? color.images[activeConfigKey] : color.image;
  const extraImages = product.extraImages || [];
  const mainImage = activeExtra !== null ? extraImages[activeExtra] : colorImage;
  const selectColor = (i) => { setActiveColor(i); setActiveExtra(null); };
  const youthSizes = product.sizes.filter((s) => sizeCategories.youth.sizes.includes(s));
  const adultSizes = product.sizes.filter((s) => sizeCategories.adult.sizes.includes(s));
  const otherSizes = product.sizes.filter((s) => !sizeCategories.youth.sizes.includes(s) && !sizeCategories.adult.sizes.includes(s));
  const lifestyle = product.lifestyle || (product.thumbnail && product.thumbnail.includes('/thumbnail') ? product.thumbnail : null);

  const isBundle = slug === 'coach-starter-kit';
  const bundleRows = [
    { k: 'cones', label: lang === 'fr' ? 'Ensembles de cônes (40)' : 'Cone sets (40)' },
    { k: 'bibs', label: lang === 'fr' ? "Dossards d'entraînement" : 'Training bibs' },
    { k: 'balls', label: lang === 'fr' ? 'Ballons de match' : 'Match balls' },
  ];
  const setQty = (k, delta) => setBundleQty((q) => ({ ...q, [k]: Math.max(0, q[k] + delta) }));

  let quoteMessage;
  if (isBundle) {
    quoteMessage = lang === 'fr'
      ? `Bonjour, je souhaite une soumission pour la Trousse de Départ de l'Entraîneur avec : ${bundleQty.cones} × ensemble de cônes (40), ${bundleQty.bibs} × dossards d'entraînement, ${bundleQty.balls} × ballons de match, 1 × pompe à ballon, 1 × sac à ballons. Merci de m'envoyer les prix.`
      : `Hello, I'd like a quote for the Coach Starter Kit with: ${bundleQty.cones} × cone set (40), ${bundleQty.bibs} × training bibs, ${bundleQty.balls} × match balls, 1 × ball pump, 1 × ball carry bag. Please send pricing.`;
  } else {
    const colourPart = product.colors.length > 1 ? `, ${lang === 'fr' ? color.nameFr : color.nameEn}` : '';
    const sizePart = activeSize ? `, ${lang === 'fr' ? 'taille' : 'size'} ${activeSize}` : '';
    quoteMessage = lang === 'fr'
      ? `Bonjour, je souhaite obtenir une soumission pour : ${loc.name} (${product.model})${colourPart}${sizePart}. Merci.`
      : `Hello, I'd like to request a quote for: ${loc.name} (${product.model})${colourPart}${sizePart}. Thank you.`;
  }
  const quoteHref = `/contact?message=${encodeURIComponent(quoteMessage)}`;

  const SizeGroup = ({ label, sizes }) =>
    sizes.length > 0 && (
      <div className="space-y-2">
        <p className="font-dm text-xs uppercase tracking-wider text-white/40">{label}</p>
        <div className="flex flex-wrap gap-2">
          {sizes.map((size) => (
            <button
              key={size}
              onClick={() => setActiveSize(size)}
              className={`min-w-[52px] font-dm text-sm py-2 px-3 border transition-colors duration-200 ${
                activeSize === size
                  ? 'border-[#CE2B37] bg-[#CE2B37] text-white'
                  : 'border-white/20 text-white/70 hover:border-white/60'
              }`}
              data-testid={`pdp-size-${size}`}
            >
              {size}
            </button>
          ))}
        </div>
      </div>
    );

  return (
    <div className="min-h-screen bg-[#0D0D0D] pt-28 md:pt-32" data-testid="product-detail-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Breadcrumb */}
        <button
          onClick={goBack}
          className="inline-flex items-center gap-2 font-dm text-xs uppercase tracking-wider text-white/50 hover:text-white transition-colors"
          data-testid="pdp-breadcrumb-back"
        >
          <ArrowLeft size={14} /> {t.pdp.back}
        </button>

        {/* Lifestyle flair banner */}
        {lifestyle && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="relative mt-5 h-[34vh] min-h-[220px] md:h-[40vh] overflow-hidden"
            data-testid="pdp-lifestyle-banner"
          >
            <img src={lifestyle} alt={`${loc.name} on the pitch`} className="w-full h-full object-cover" style={{ objectPosition: product.bannerPosition || 'center' }} />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D] via-black/30 to-transparent" />
            <div className="absolute bottom-5 left-5 md:bottom-8 md:left-8">
              <span className="font-dm text-[10px] tracking-[3px] text-[#CE2B37] uppercase">{product.model}</span>
              <h1 className="font-bebas text-3xl md:text-5xl text-white tracking-tight leading-none mt-1">{loc.name}</h1>
            </div>
          </motion.div>
        )}

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 mt-8 pb-20">
          {/* Image Gallery */}
          <div className="space-y-4">
            <motion.div
              key={mainImage}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="aspect-square bg-[#FAFAFA] overflow-hidden"
              data-testid="pdp-main-image"
            >
              <img
                src={mainImage}
                data-testid="pdp-main-image-element"
                alt={`${loc.name} - ${lang === 'fr' ? color.nameFr : color.nameEn}`}
                className="w-full h-full object-cover"
              />
            </motion.div>

            {/* Thumbnails: colors + extra images */}
            <div className="flex flex-wrap gap-3">
              {product.colors.map((c, i) => (
                <button
                  key={c.name}
                  onClick={() => selectColor(i)}
                  className={`w-20 h-20 bg-[#FAFAFA] overflow-hidden border-2 transition-colors duration-200 ${
                    activeExtra === null && activeColor === i ? 'border-[#CE2B37]' : 'border-transparent hover:border-white/30'
                  }`}
                  data-testid={`pdp-thumb-${c.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <img src={c.image} alt={lang === 'fr' ? c.nameFr : c.nameEn} className="w-full h-full object-cover" />
                </button>
              ))}
              {extraImages.map((src, i) => (
                <button
                  key={src}
                  onClick={() => setActiveExtra(i)}
                  className={`w-20 h-20 bg-[#FAFAFA] overflow-hidden border-2 transition-colors duration-200 ${
                    activeExtra === i ? 'border-[#CE2B37]' : 'border-transparent hover:border-white/30'
                  }`}
                  data-testid={`pdp-extra-thumb-${i}`}
                >
                  <img src={src} alt={`${loc.name} ${i + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          {/* Details */}
          <div className="space-y-8">
            <div>
              {product.badge && (
                <span className="inline-block bg-[#CE2B37] text-white font-dm text-xs px-3 py-1 uppercase tracking-wider mb-4">
                  {product.badge === 'New' ? t.pdp.newBadge : product.badge}
                </span>
              )}
              <h1 className="font-bebas text-4xl md:text-5xl text-white tracking-tight leading-none" data-testid="pdp-product-name">
                {loc.name}
              </h1>
              <p className="font-dm text-sm text-white/40 mt-2">
                {t.pdp.model}: {product.model}
              </p>
              <TricolorStripe className="mt-4" />
            </div>

            <p className="font-dm text-base text-white/70 leading-relaxed" data-testid="pdp-description">
              {loc.description}
            </p>

            {/* Colors */}
            <div className="space-y-3">
              <p className="font-dm text-xs uppercase tracking-wider text-white/40">
                {t.pdp.colours}: <span className="text-white/80" data-testid="pdp-selected-colour">{lang === 'fr' ? color.nameFr : color.nameEn}</span>
              </p>
              <div className="flex flex-wrap gap-3">
                {product.colors.map((c, i) => (
                  <button
                    key={c.name}
                    onClick={() => selectColor(i)}
                    className={`w-9 h-9 rounded-full border-2 transition-all duration-200 ${
                      activeExtra === null && activeColor === i ? 'border-[#CE2B37] scale-110' : 'border-white/20 hover:border-white/50'
                    }`}
                    style={{ background: c.secondaryHex ? `linear-gradient(135deg, ${c.hex} 50%, ${c.secondaryHex} 50%)` : c.hex }}
                    title={lang === 'fr' ? c.nameFr : c.nameEn}
                    aria-label={lang === 'fr' ? c.nameFr : c.nameEn}
                    aria-pressed={activeExtra === null && activeColor === i}
                    data-testid={`pdp-color-${c.name.toLowerCase().replace(/\s+/g, '-')}`}
                  />
                ))}
              </div>
            </div>

            {/* Configuration (e.g. Jacket / Pants / Kit) */}
            {configs.length > 0 && (
              <div className="space-y-3" data-testid="pdp-configuration">
                <p className="font-dm text-xs uppercase tracking-wider text-white/40">
                  {t.pdp.configuration}: <span className="text-white/80">{lang === 'fr' ? configs[activeConfigIdx].nameFr : configs[activeConfigIdx].nameEn}</span>
                </p>
                <div className="flex flex-wrap gap-2">
                  {configs.map((cfg, i) => (
                    <button
                      key={cfg.key}
                      onClick={() => { setActiveConfigIdx(i); setActiveExtra(null); }}
                      className={`font-dm text-sm py-2 px-4 border transition-colors duration-200 ${
                        activeExtra === null && activeConfigIdx === i
                          ? 'border-[#CE2B37] bg-[#CE2B37] text-white'
                          : 'border-white/20 text-white/70 hover:border-white/60'
                      }`}
                      data-testid={`pdp-config-${cfg.key}`}
                    >
                      {lang === 'fr' ? cfg.nameFr : cfg.nameEn}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Sizes */}
            {!isBundle && (
              <div className="space-y-4">
                <p className="font-dm text-xs uppercase tracking-wider text-white/40">{t.pdp.sizeGuide}</p>
                <SizeGroup label={t.pdp.youthSizes} sizes={youthSizes} />
                <SizeGroup label={t.pdp.adultSizes} sizes={adultSizes} />
                <SizeGroup label="" sizes={otherSizes} />
              </div>
            )}

            {/* Bundle builder (Coach Starter Kit) */}
            {isBundle && (
              <div className="space-y-4" data-testid="pdp-bundle-builder">
                <p className="font-dm text-xs uppercase tracking-wider text-white/40">
                  {lang === 'fr' ? 'Composez votre trousse' : 'Build your kit'}
                </p>
                <div className="space-y-3">
                  {bundleRows.map((row) => (
                    <div
                      key={row.k}
                      className="flex items-center justify-between border border-white/15 px-4 py-3"
                      data-testid={`bundle-row-${row.k}`}
                    >
                      <span className="font-dm text-sm text-white/80">{row.label}</span>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setQty(row.k, -1)}
                          className="w-8 h-8 flex items-center justify-center border border-white/20 text-white hover:border-[#CE2B37] transition-colors"
                          data-testid={`bundle-${row.k}-minus`}
                          aria-label={`decrease ${row.k}`}
                        >
                          −
                        </button>
                        <span className="w-8 text-center font-dm text-sm text-white" data-testid={`bundle-${row.k}-qty`}>
                          {bundleQty[row.k]}
                        </span>
                        <button
                          onClick={() => setQty(row.k, 1)}
                          className="w-8 h-8 flex items-center justify-center border border-white/20 text-white hover:border-[#CE2B37] transition-colors"
                          data-testid={`bundle-${row.k}-plus`}
                          aria-label={`increase ${row.k}`}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="font-dm text-[11px] text-white/40">
                  {lang === 'fr'
                    ? 'Une pompe à ballon et un sac à ballons sont inclus dans chaque trousse.'
                    : 'A ball pump and a ball carry bag are included in every kit.'}
                </p>
              </div>
            )}

            {/* Features */}
            <div className="space-y-3">
              <p className="font-dm text-xs uppercase tracking-wider text-white/40">{t.pdp.features}</p>
              <ul className="space-y-2">
                {loc.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 font-dm text-sm text-white/70">
                    <Check size={16} className="text-[#CE2B37] mt-0.5 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>

            {/* Quote CTA */}
            <div className="space-y-3 pt-2">
              <Link
                to={quoteHref}
                className="block w-full text-center bg-[#CE2B37] text-white font-bebas text-lg tracking-wider py-4 hover:bg-[#a82230] transition-colors duration-200"
                data-testid="pdp-request-quote"
              >
                {t.pdp.requestQuote}
              </Link>
              {canCustomize && (
                <Link
                  to="/custom-uniforms"
                  className="block w-full text-center border border-white/20 text-white font-bebas text-lg tracking-wider py-4 hover:border-white/60 transition-colors duration-200"
                  data-testid="pdp-customize"
                >
                  {t.pdp.customCta}
                </Link>
              )}
              <p className="font-dm text-xs text-white/40 leading-relaxed pt-1">{t.pdp.quoteNote}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
